"use client"

import { motion } from "motion/react"
import { useState } from "react"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

type Props = {
  name: string,
  description: string,
  emoji: string, // Changed from icon to emoji
  icon?: LucideIcon, // Added optional right icon
  onNavigate: () => void,
  className?: string
}

const ThemeNavigationCard = ({ name, description, emoji, icon: RightIcon = ChevronRight, onNavigate, className }: Props) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      data-testid={name}
      onClick={onNavigate}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(className, "relative cursor-pointer group h-20 flex items-center gap-4 px-5 flex-1 border-2 border-purple-200 hover:border-purple-300 rounded-2xl overflow-hidden bg-white hover:shadow-sm transition-all duration-200")}
    >
      {/* Subtle background pattern */}
      <div
        className={cn(
          "absolute inset-0 z-0 opacity-30",
          "[background-size:20px_20px]",
          "[background-image:linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)]",
        )}
      />

      {/* Radial gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white to-purple-50/30"></div>

      {/* Emoji instead of icon */}
      <div className="flex items-center justify-center size-12 text-2xl z-10 group-hover:scale-110 transition-transform duration-300">
        {emoji}
      </div>

      {/* Content */}
      <div className="flex-1 space-y-0 z-10 min-w-0">
        <motion.h3
          initial={false}
          animate={{
            fontSize: isHovered ? "1.3rem" : "1.1rem",
          }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
          className="font-semibold text-purple-600 group-hover:text-purple-700 transition-colors leading-tight"
        >
          {name}
        </motion.h3>
        <motion.p
          className="text-xs text-gray-500 leading-relaxed"
          animate={{
            opacity: isHovered ? 0.7 : 1,
            y: isHovered ? -2 : 0,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {description}
        </motion.p>
      </div>

      {/* Right icon - always visible */}
      <div className="flex items-center justify-center z-10">
        <motion.div
          animate={{
            scale: isHovered ? 1.1 : 1,
            x: isHovered ? 2 : 0,
          }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex items-center justify-center size-8 rounded-full bg-purple-100 group-hover:bg-purple-200 transition-colors duration-200"
        >
          <RightIcon className="size-4 text-purple-500" />
        </motion.div>
      </div>
    </div>
  )
}

export default ThemeNavigationCard
